import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';

enum ToastMessageIdEnum {
  CROSSPLAN_UPDATE_ERROR = 'crossPlanUpdateError',
  CROSSPLAN_REFERENCE_IN_PROGRESS = 'creatingReference',
  CROSSPLAN_REFERENCE_CREATED = 'referenceCreated',
  CROSSPLAN_TIMELINES_NOT_SUPPORTED = 'crossPlanTimelinesNotSupported',
  CROSSPLAN_SECTION_ERROR = 'crossPlanSectionError',
  ACCESS_DENIED_ERROR = 'accessDeniedError',
  CONVERTING_TO_LIST = 'convertingToList',
  REMOTE_ARTIFACT_CREATED = 'remoteArtifactCreated',
  FAILED_GENERATING = 'failedGenerating',
  FAILED_GENERATING_SHORTHAND = 'failedGeneratingShorthand',
  FAILED_DELETING_USER = 'failedDeletingUser',
  FAILED_DELETING_LINK = 'failedDeletingLink',
  FAILED_CREATING_LINK = 'failedCreatingLink',
  FAILED_DELETING_ARTIFACT = 'failedDeletingArtifact',
  FAILED_REACTIVATING_USER = 'failedReactivatingUser',
  FAILED_COPY = 'failedCopy',
  FAILED_DOWNLOAD = 'failedDownload',
  FAILED_PRINT = 'failedPrint',
  SHORTHAND_LANGUAGE_NOT_SUPPORTED = 'shorthandLanguageNotSupported',
  DIRTY_DEBUG_MESSAGE = 'dirtyDebugMessage',
  CSV_EXPORT_STARTED = 'csvExportStarted',
  CSV_EXPORT_FAILED = 'csvExportFailed',
  CSV_EXPORT_COMPLETED = 'csvExportCompleted',
  IMAGE_TIMEOUT = 'imageTimeout',
  TEMPLATE_ARTIFACT = 'templateArtifact',
  FEATURE_FLAG_SUCCESS = 'featureFlagSuccess',
  FEATURE_FLAG_FAILED = 'featureFlagFailed',
  STARTER_PLAN_SET_SUCCESS = 'starterPlanSetSuccess',
  STARTER_PLAN_SET_ERROR = 'starterPlanSetError',
  ADD_MEMBERS_SUCCESS = 'addMembersSuccess',
  ADD_MEMBERS_PARTIAL_FAILURE = 'addMembersPartialFailure',
  ADD_MEMBERS_FAILURE = 'addMembersFailure',
  REQUEST_ACCESS_SUCCESS = 'requestAccessSuccess',
  REQUEST_ACCESS_PARTIAL_FAILURE = 'requestAccessPartialFailure',
  REQUEST_ACCESS_FAILURE = 'requestAccessFailure',
  FAILED_DELETING_CHAT = 'failedDeletingChat',
  FAILED_PINNING_CHAT = 'failedPinningChat',
  FAILED_RENAMING_CHAT = 'failedRenamingChat',
  FAILED_OPENING_CHAT = 'failedOpeningChat',
  BACKGROUND_JOB_START_FAILED = 'backgroundJobStartFailed',
  BACKGROUND_JOB_NOT_FOUND = 'backgroundJobNotFound',
  BACKGROUND_JOB_TERMINATE_FAILED = 'backgroundJobTerminateFailed',
  BACKGROUND_JOB_RUN_NOT_FOUND = 'backgroundJobRunNotFound',
  BACKGROUND_JOB_NOT_TERMINAL = 'backgroundJobNotTerminal',
  BACKGROUND_JOB_DELETE_FAILED = 'backgroundJobDeleteFailed',
  BACKGROUND_JOB_DELETE_NOT_FOUND = 'backgroundJobDeleteNotFound',
  BACKGROUND_JOB_DELETE_CONFLICT = 'backgroundJobDeleteConflict',
  RATE_LIMIT_EXCEEDED = 'rateLimitExceeded',
}

const toastMessages: Record<ToastMessageIdEnum, MessageDescriptor> = defineMessages({
  crossPlanUpdateError: {
    id: 'toast.crossPlanUpdateError',
    defaultMessage: 'Cannot modify cross plan artifact',
  },
  creatingReference: {
    id: 'toast.creatingCrossPlanReference',
    defaultMessage: 'Creating reference\u2026',
  },
  referenceCreated: {
    id: 'toast.createdCrossPlanReference',
    defaultMessage: 'Reference created!',
  },
  crossPlanTimelinesNotSupported: {
    id: 'toast.crossPlanTimelinesNotSupported',
    defaultMessage: 'Cross plan cards cannot be added to timelines',
  },
  crossPlanSectionError: {
    id: 'toast.crossPlanSectionError',
    defaultMessage: 'Unable to cross plan section',
  },
  accessDeniedError: {
    id: 'toast.accessDeniedError',
    defaultMessage: 'You do not have access to do that action',
  },
  convertingToList: {
    id: 'toast.convertingToList',
    defaultMessage: 'Converting to list\u2026',
  },
  remoteArtifactCreated: {
    id: 'toast.remoteArtifactCreated',
    defaultMessage: 'Remote artifact ({artifactType}) created. ID: {artifactId}',
  },
  failedGeneratingShorthand: {
    id: 'toast.failedGeneratingShorthand',
    defaultMessage: 'Failed to generate card shorthand.',
  },
  failedGenerating: {
    id: 'toast.failedGenerating',
    defaultMessage: 'Failed to generate',
  },
  failedDeletingUser: {
    id: 'toast.failedDeletingUser',
    defaultMessage: 'Failed to delete user',
  },
  failedDeletingLink: {
    id: 'toast.failedDeletingLink',
    defaultMessage: 'Could not delete link. Please try again.',
  },
  failedCreatingLink: {
    id: 'toast.failedCreatingLink',
    defaultMessage: 'Could not create link. Please try again.',
  },
  failedDeletingArtifact: {
    id: 'toast.failedDeletingArtifact',
    defaultMessage: 'Could not delete. Please try again.',
  },
  failedReactivatingUser: {
    id: 'toast.failedReactivatingUser',
    defaultMessage: 'Failed to reactivate user',
  },
  failedCopy: {
    id: 'toast.failedCopy',
    defaultMessage: 'Failed to copy',
  },
  failedDownload: {
    id: 'toast.failedDownload',
    defaultMessage: 'Failed to download',
  },
  failedPrint: {
    id: 'toast.failedPrint',
    defaultMessage: 'Failed to print',
  },
  shorthandLanguageNotSupported: {
    id: 'toast.shorthandLanguageNotSupported',
    defaultMessage: 'Language not supported',
  },
  dirtyDebugMessage: {
    id: 'toast.dirtyDebugMessage',
    defaultMessage: '{debugMessage}!',
  },
  csvExportStarted: {
    id: 'toast.csvExportStarted',
    defaultMessage: 'Exporting CSV\u2026',
  },
  csvExportFailed: {
    id: 'toast.csvExportFailed',
    defaultMessage: 'Failed to export CSV',
  },
  csvExportCompleted: {
    id: 'toast.csvExportCompleted',
    defaultMessage: 'CSV export completed',
  },
  imageTimeout: {
    id: 'toast.imageTimeout',
    defaultMessage: 'Image {imageKey} timed out and may have an issue',
  },
  templateArtifact: {
    id: 'toast.templateArtifact',
    defaultMessage: 'Failed to create artifact from template',
  },
  featureFlagFailed: {
    id: 'toast.featureFlagFailed',
    defaultMessage: 'Failed to update feature flag. Please try again.',
  },
  featureFlagSuccess: {
    id: 'toast.featureFlagSuccess',
    defaultMessage: 'Feature flag updated successfully.',
  },
  starterPlanSetSuccess: {
    id: 'toast.starterPlanSetSuccess',
    defaultMessage: 'Successfully set starter plan',
  },
  starterPlanSetError: {
    id: 'toast.starterPlanSetError',
    defaultMessage: 'An error occurred while setting the starter plan',
  },
  addMembersSuccess: {
    id: 'toast.addMembersSuccess',
    defaultMessage: 'Successfully added {count, plural, =1 {member} other {members}}',
  },
  addMembersPartialFailure: {
    id: 'toast.addMembersPartialFailure',
    defaultMessage:
      'Added {successCount} {successCount, plural, =1 {member} other {members}}, but {failureCount} {failureCount, plural, =1 {failed} other {failed}}',
  },
  addMembersFailure: {
    id: 'toast.addMembersFailure',
    defaultMessage: 'Failed to add {count, plural, =1 {member} other {members}}',
  },
  requestAccessSuccess: {
    id: 'toast.requestAccessSuccess',
    defaultMessage: 'Requested access for {count, plural, =1 {this person} other {these people}}',
  },
  requestAccessPartialFailure: {
    id: 'toast.requestAccessPartialFailure',
    defaultMessage:
      'Requested access for {successCount} {successCount, plural, =1 {person} other {people}}, but {failureCount} {failureCount, plural, =1 {request failed} other {requests failed}}',
  },
  requestAccessFailure: {
    id: 'toast.requestAccessFailure',
    defaultMessage:
      'Failed to request access for {count, plural, =1 {this person} other {these people}}',
  },
  failedDeletingChat: {
    id: 'toast.failedDeletingChat',
    defaultMessage: 'Failed to delete chat',
  },
  failedPinningChat: {
    id: 'toast.failedPinningChat',
    defaultMessage: 'Failed to pin/unpin chat',
  },
  failedRenamingChat: {
    id: 'toast.failedRenamingChat',
    defaultMessage: 'Failed to rename chat',
  },
  failedOpeningChat: {
    id: 'toast.failedOpeningChat',
    defaultMessage: 'Failed to open chat',
  },
  backgroundJobStartFailed: {
    id: 'toast.backgroundJobStartFailed',
    defaultMessage:
      'An internal server error occurred while starting the job. Please contact support.',
  },
  backgroundJobNotFound: {
    id: 'toast.backgroundJobNotFound',
    defaultMessage: 'Background job not found. The job ID may no longer be registered.',
  },
  backgroundJobTerminateFailed: {
    id: 'toast.backgroundJobTerminateFailed',
    defaultMessage:
      'An internal server error occurred while terminating the job. Please contact support.',
  },
  backgroundJobRunNotFound: {
    id: 'toast.backgroundJobRunNotFound',
    defaultMessage: 'Job run not found. It may have already completed or been deleted.',
  },
  backgroundJobNotTerminal: {
    id: 'toast.backgroundJobNotTerminal',
    defaultMessage: 'This job cannot be terminated',
  },
  backgroundJobDeleteFailed: {
    id: 'toast.backgroundJobDeleteFailed',
    defaultMessage:
      'An internal server error occurred while deleting the job run. Please contact support.',
  },
  backgroundJobDeleteNotFound: {
    id: 'toast.backgroundJobDeleteNotFound',
    defaultMessage: 'Job run not found. It may have already been deleted.',
  },
  backgroundJobDeleteConflict: {
    id: 'toast.backgroundJobDeleteConflict',
    defaultMessage: 'This job run cannot be deleted while it is still running or terminating.',
  },
  rateLimitExceeded: {
    id: 'toast.rateLimitExceeded',
    defaultMessage: 'The system is currently under high load.',
  },
});

export { ToastMessageIdEnum };
export default toastMessages;
